import type { TokenEncoder } from 'prosemirror-changeset';

import type { Mark, Node as PMNode } from '@atlaskit/editor-prosemirror/model';

/**
 * Attribute-aware token encoder for `prosemirror-changeset`.
 *
 * The library's default encoder reduces a node's open token to `node.type.name`,
 * ignoring attributes. An attribute-only change (e.g. recolouring a table cell's
 * `background`) therefore tokenises identically on both sides and reports no
 * change at all.
 *
 * This encoder folds an allow-listed set of attributes into the open token so
 * such changes register. Everything else encodes exactly as the default. The
 * allow-list is deliberately narrow — folding in ephemeral attrs like `localId`
 * would produce phantom diffs.
 */

const DIFFED_ATTRS_BY_NODE_TYPE: Record<string, readonly string[]> = {
	tableCell: ['background', 'colspan', 'rowspan'],
	tableHeader: ['background', 'colspan', 'rowspan'],
};

/**
 * Builds a stable composite token. Iterates the allow-list rather than
 * `Object.keys` so ordering is deterministic for string comparison.
 */
const encodeNodeWithAttrs = (node: PMNode, attrNames: readonly string[]): string => {
	const attrs = node.attrs ?? {};
	// Deterministic order: iterate the allow-list, not `Object.keys(attrs)`.
	const parts = attrNames.map((name) => `${name}=${JSON.stringify(attrs[name] ?? null)}`);
	return `${node.type.name}|${parts.join('|')}`;
};

/**
 * Identical to the library default except that allow-listed node types encode
 * their allow-listed attributes into the open token. Characters and node-end
 * tokens are left as-is, hence the `string | number` token type.
 */
export const attrAwareTokenEncoder: TokenEncoder<string | number> = {
	encodeCharacter: (char: number, _marks: readonly Mark[]) => char,
	encodeNodeStart: (node: PMNode) => {
		const attrNames = DIFFED_ATTRS_BY_NODE_TYPE[node.type.name];
		if (attrNames) {
			return encodeNodeWithAttrs(node, attrNames);
		}
		return node.type.name;
	},
	encodeNodeEnd: (node: PMNode) => -typeID(node.type),
	compareTokens: (a, b) => a === b,
};

/**
 * Mirrors the library's private `typeID` so node-end tokens match the default
 * encoding exactly. Reimplemented here because it is not exported.
 */
function typeID(type: PMNode['type']): number {
	const cache: Record<string, number> =
		type.schema.cached.changeSetIDs || (type.schema.cached.changeSetIDs = Object.create(null));
	let id = cache[type.name];
	if (id == null) {
		cache[type.name] = id = Object.keys(type.schema.nodes).indexOf(type.name) + 1;
	}
	return id;
}
